/**
 * Availability strip over the board's selected window (7/30/90 days), one row
 * per site — the status-page pattern: a thin bar per day, oldest on the left,
 * with "N days ago" / "Today" anchors.
 * L1 altitude: a heat row per site (magnitude over a time × entity grid,
 * dashboard design rules §2), and every site name is a door into the site's
 * L2 drawer (§4.1), whose open state lives in the URL (`?site=<id>`, §4.2).
 *
 * Built on `StatusStrip`, the design system's heat-row primitive (see the
 * HealthBand pattern's "Heat Row" story). `@norican/react` has no dedicated
 * heatmap/uptime-bar component; the day-per-cell strip is exactly this
 * board's flagship form.
 *
 * WORKAROUND (design-system gap): StatusStrip is built for ~12 labelled cells
 * carrying values, so two things need nudging at 90 empty cells:
 *   1. `h-8` — cells size from their content, so an empty row collapses to a
 *      few pixels.
 *   2. `bg-border` on no-data cells — the DS `neutral` tone is `bg-muted`,
 *      which is nearly indistinguishable from the card surface on the dark
 *      theme, leaving "no data" invisible rather than reading as a grey bar.
 * Both are token-backed Tailwind utilities passed via `className` (they win over
 * DS styling because component CSS ships in `@layer norican`) — no DS CSS is
 * forked. Everything else (equal widths via `flex:1 1 0`, the 2px gutter, the
 * RAG tints) is the component's own styling. If StatusStrip gains a dense
 * variant and a visible neutral tone, drop both utilities.
 *
 * Days with no recorded checks stay `neutral` ("no data") rather than green, so
 * an unobserved day is never mistaken for a healthy one. History accrues from
 * the first poll cycle onward, so early on most of the strip is empty.
 */

import { Card, cn, EmptyState, Meter, StatusStrip, Typography } from "@norican/react";

import type { MonitoringHistory, SiteUptimeHistory } from "@/lib/api";
import { useDrawerParam } from "@/lib/drill";

import {
  type CellStatus,
  dayStatus,
  dayTitle,
  formatDay,
  HEALTHY_PCT,
  SHAKY_PCT,
  SITE_DETAIL_PARAM,
  uptimeTone,
} from "./status";

/** One site's day-per-cell availability strip — reused by the site drawer. */
export function SiteHistoryStrip({ site, days }: { site: SiteUptimeHistory; days: number }) {
  return (
    <div className="flex flex-col gap-2">
      <StatusStrip aria-label={`${days}-day availability for ${site.name}`}>
        {site.days.map((day) => {
          const status = dayStatus(day);
          return (
            <StatusStrip.Cell
              key={day.day}
              status={status}
              title={dayTitle(day)}
              // See the WORKAROUND note above.
              className={cn("h-8", status === "neutral" && "bg-border")}
            />
          );
        })}
      </StatusStrip>
      {/* The uptime figure itself lives in the SiteUptimeMeter beside the row. */}
      <div className="text-fg-muted flex items-baseline justify-between gap-4 text-xs">
        <span>{days} days ago</span>
        <span>Today</span>
      </div>
    </div>
  );
}

/** The window-uptime Meter with its honest coverage caption (rules §5). */
export function SiteUptimeMeter({ site, days }: { site: SiteUptimeHistory; days: number }) {
  const pct = site.window_uptime_pct;
  const recorded = site.recorded_days ?? 0;
  if (pct == null) {
    return (
      <Typography variant="caption" tone="muted">
        Uptime: no checks recorded yet
      </Typography>
    );
  }
  const tone = uptimeTone(pct);
  return (
    <Meter.Root
      value={pct}
      min={0}
      max={100}
      size="sm"
      tone={tone === "neutral" ? "accent" : tone}
      aria-valuetext={`${pct.toFixed(2)}% uptime over ${recorded} of ${days} recorded days for ${site.name}`}
    >
      <div className="mb-1 flex items-baseline justify-between gap-3">
        <Meter.Label>
          <Typography variant="caption" tone="muted">
            Uptime
          </Typography>
        </Meter.Label>
        <Meter.Value>
          {() => (
            <Typography variant="caption" tone="muted" tabular>
              {pct.toFixed(2)}% · {recorded} of {days} days recorded
            </Typography>
          )}
        </Meter.Value>
      </div>
      <Meter.Track>
        <Meter.Indicator />
      </Meter.Track>
    </Meter.Root>
  );
}

interface UptimeHeatmapProps {
  /** History payload, already scoped by the board's site filter. */
  history: MonitoringHistory;
  /** Sites to render (the filtered slice of `history.sites`). */
  sites: SiteUptimeHistory[];
}

export function UptimeHeatmap({ history, sites }: UptimeHeatmapProps) {
  const drawer = useDrawerParam(SITE_DETAIL_PARAM);
  const recordedDays = Math.max(...history.sites.map((site) => site.recorded_days ?? 0), 0);

  return (
    <Card variant="outline">
      <Card.Header>
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <Typography variant="heading-4" as="h2">
            {history.days}-day availability
          </Typography>
          <Typography variant="body-sm" tone="muted">
            Recording since {formatDay(history.end_day)} · {recordedDays}{" "}
            {recordedDays === 1 ? "day" : "days"} of history
          </Typography>
        </div>
      </Card.Header>
      <Card.Body>
        {sites.length === 0 ? (
          <EmptyState
            size="sm"
            title="No sites match the filter"
            description="Clear the site filter above to see the whole estate."
          />
        ) : (
          <div className="flex flex-col gap-7">
            {sites.map((site) => (
              <div key={site.id} className="flex flex-col gap-2">
                <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-1">
                  <div className="flex items-baseline gap-3">
                    <button
                      type="button"
                      onClick={() => drawer.open(site.id)}
                      className="text-fg hover:text-accent text-left text-sm font-medium underline-offset-2 hover:underline"
                      aria-label={`Open details for ${site.name}`}
                    >
                      {site.name}
                    </button>
                    <span className="text-fg-muted text-xs">{site.business}</span>
                  </div>
                  <div className="w-64 max-w-full">
                    <SiteUptimeMeter site={site} days={history.days} />
                  </div>
                </div>

                <SiteHistoryStrip site={site} days={history.days} />
              </div>
            ))}
          </div>
        )}

        <div className="border-border mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 border-t pt-4">
          <Typography variant="caption" as="span" className="font-semibold tracking-wide uppercase">
            Legend
          </Typography>
          <LegendKey status="positive" label={`≥ ${HEALTHY_PCT}% uptime`} />
          <LegendKey status="caution" label={`${SHAKY_PCT}–${HEALTHY_PCT}%`} />
          <LegendKey status="negative" label={`< ${SHAKY_PCT}%`} />
          <LegendKey status="neutral" label="No data" />
        </div>
      </Card.Body>
    </Card>
  );
}

function LegendKey({ status, label }: { status: CellStatus; label: string }) {
  return (
    <span className="text-fg-muted flex items-center gap-2 text-xs">
      {/* A single-cell strip reuses the exact tints the bars above use. */}
      <span className="inline-block w-8">
        <StatusStrip aria-hidden="true">
          <StatusStrip.Cell
            status={status}
            className={cn("h-3", status === "neutral" && "bg-border")}
          />
        </StatusStrip>
      </span>
      {label}
    </span>
  );
}
